import React, { useState, useEffect, forwardRef } from "react";
import ModalImage from "react-modal-image";
import { uploadImage } from "services/profile.service";
import { confirm } from "components";

const FilePicker = forwardRef((props, ref) => {
  const [values, setValues] = useState({ errormsg: "", preview: props.initialUrl || null, fileName: "" });

  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (props.initialUrl) {
      setValues((prev) => ({ ...prev, preview: props.initialUrl }));
    }
  }, [props.initialUrl]);

  const removeUpload = (e) => {
    if (e) e.preventDefault();
    props.onPick("attachment", { file: null, url: "", loading: false });
    resetFileState();
  };

  const handleRemoveClick = (e) => {
    e.preventDefault();
    confirm({
      message: "You want to remove this <b>Attachment</b>",
      onConfirm: () => removeUpload(),
    });
  };

  const resetFileState = () => {
    setValues({ preview: null, errormsg: "", fileName: "" });
  };

  function offErrorAfterInterval() {
    setTimeout(() => {
      setValues((prev) => ({ ...prev, errormsg: "" }));
    }, 3000);
  }

  const handleFileChange = async (e) => {
    e.preventDefault();
    const file = e.target.files[0];

    try {
      if (file) {
        // Validate file type (image or PDF)
        const allowedTypes = ["image/jpeg", "image/png", "image/gif", "application/pdf"];
        if (!allowedTypes.includes(file.type)) {
          setValues({
            errormsg: "Only Image or PDF files are allowed.",
            preview: null,
            fileName: ""
          });
          offErrorAfterInterval();
          return;
        }


        // Validate file size (10MB limit)
        if (file.size <= 10485760 && e.target.files.length === 1) {
          const objectUrl = URL.createObjectURL(file);
          setValues({ errormsg: "", preview: objectUrl, fileName: file.name });
          props.onPick("attachment", { file: file, url: objectUrl, loading: true });
          const result = await uploadImage(file, setProgress);
          props.onPick("attachment", { url: result._url, file: file, loading: false });
        } else if (e.target.files.length > 1) {
          setValues({ errormsg: "Only one file allowed", preview: null, fileName: "" });
          props.onPick("attachment", { url: null, file: null, loading: false });
          offErrorAfterInterval();
        } else {
          setValues({ errormsg: "File exceeds the size limit of 10MB.", preview: null, fileName: "" });
          props.onPick("attachment", { url: null, file: null, loading: false });
          offErrorAfterInterval();
        }
      }
    } catch (error) {
      console.log("Error while uploading image:", error);
      if (error.message === "Filename contains invalid characters.") {
        setValues({ errormsg: "Filename contains invalid special characters (),=+-", preview: null, fileName: "" });
      } else {
        setValues({ errormsg: "Try Again, File Upload Failed", preview: null, fileName: "" });
      }
      props.onPick("attachment", { url: null, file: null, loading: false });
      offErrorAfterInterval();
    } finally {
      setProgress(0);
    }
  };

  const renderProgressOverlay = (progress) => {
    return (
      <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center px-2" style={{ background: "rgba(255,255,255,0.85)" }}>
        <div className="spinner-border text-app spinner-border-sm mb-2" role="status">
        </div>
        <div className="progress w-100" style={{ height: "4px" }}>
          <div
            className="progress-bar bg-app"
            role="progressbar"
            style={{ width: `${progress}%` }}
            aria-valuenow={progress}
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
        <small className="text-app mt-1" style={{ fontSize: "10px" }}>{Math.round(progress)}%</small>
      </div>
    );
  };

  // const handleModalOpen = () => {
  //   const modal = new window.bootstrap.Modal(document.getElementById('deleteAttachmentModal'));
  //   modal.show();
  // };

  const isPDF = values.fileName?.toLowerCase().includes(".pdf") || values.preview?.toLowerCase().includes(".pdf");

  return (
    <div className="upload-bill-container mb-5">
      {/* Hidden file input - ref exposed directly */}
      <input
        type="file"
        id="upload_bills"
        ref={ref}
        className="d-none"
        onChange={handleFileChange}
        accept="image/*,application/pdf"
        aria-label="Upload Bill"
      />

      {/* Preview Section */}
      {values.preview && (
        <div className="position-relative d-inline-block mt-2">
          <div className="position-relative overflow-hidden rounded shadow-sm border border-light">
            {isPDF ? (
              <a href={values.preview} className="text-decoration-none text-app" target="_blank" rel="noopener noreferrer">
                <div className="p-3 d-flex flex-column align-items-center bg-light" style={{ width: "120px" }}>
                  <i className="bi bi-file-earmark-pdf-fill text-danger" style={{ fontSize: "3rem" }}></i>
                  <small className="text-truncate w-100 text-center mt-1">
                    {values.fileName || "Attachment"}
                  </small>
                </div>
              </a>
            ) : (
              <ModalImage
                small={values.preview}
                large={values.preview}
                showRotate={true}
                hideDownload={true}
                className="uploadbillpreview"
              />
            )}

            {/* Loading */}
            {props.loading && renderProgressOverlay(progress)}
          </div>

          {/* Remove button */}
          {!props.loading && (
            <button
              onClick={handleRemoveClick}
              className="btn btn-sm btn-danger rounded-circle position-absolute"
              style={{ top: "-10px", right: "-10px", width: "24px", height: "24px", padding: "0" }}
              title="Remove Attachment"
              type="button"
            >
              <i className="bi bi-x fs-6"></i>
            </button>
          )}
        </div>
      )}

      {/* Error message */}
      {values.errormsg && (
        <div className="alert alert-danger mt-2 py-2 px-3 d-flex align-items-center mb-0" role="alert">
          <i className="bi bi-exclamation-circle-fill me-2"></i>
          <small>{values.errormsg}</small>
        </div>
      )}

      {/* <ConfirmationModal id="deleteAttachmentModal" onClick={removeUpload} /> */}
    </div>
  );
});

export default FilePicker;
